import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User, Phone, Building2, MapPin, Hammer, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import CityCombobox from "@/components/CityCombobox";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import ToolsBackdrop from "@/components/ToolsBackdrop";

export default function Register() {
  const { role } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { register } = useAuth();
  const isPro = role === "pro";

  const [trades, setTrades] = useState([]);
  const [cityObj, setCityObj] = useState(null);
  const [busy, setBusy] = useState(false);
  const [siretCheck, setSiretCheck] = useState(null);

  const [form, setForm] = useState({
    email: "", password: "", name: "",
    role,
    company: "", phone: "",
    identifier_type: "siret", identifier: "",
    logo: null,
    trade: "", radius_km: 15,
    accepted_charter: !!loc.state?.charter,
  });

  useEffect(() => {
    api.get("/trades").then((r) => setTrades(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const verifyIdentifier = async () => {
    const digits = (form.identifier || "").replace(/\D/g, "");
    if (digits.length !== (form.identifier_type === "siren" ? 9 : 14)) {
      toast.error(`Merci de renseigner un ${form.identifier_type.toUpperCase()} valide`);
      return;
    }
    try {
      const { data } = await api.get("/verify/siret", { params: { number: digits } });
      if (data.found) {
        setSiretCheck(data);
        toast.success(`Entreprise trouvée : ${data.denomination || "OK"}`);
      } else {
        setSiretCheck({ found: false });
        toast.error("Aucune entreprise trouvée avec ce numéro (INSEE)");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Email, mot de passe et nom requis");
      return;
    }
    if (isPro && (!form.trade || !cityObj)) {
      toast.error("Métier et ville requis pour les pros");
      return;
    }
    if (!isPro && !cityObj) {
      toast.error("Merci de choisir votre ville");
      return;
    }
    if (isPro) {
      const digits = (form.identifier || "").replace(/\D/g, "");
      const needed = form.identifier_type === "siren" ? 9 : 14;
      if (digits.length !== needed) {
        toast.error(`Le numéro ${form.identifier_type.toUpperCase()} doit contenir ${needed} chiffres`);
        return;
      }
    }
    setBusy(true);
    try {
      const user = await register({
        ...form,
        city: cityObj?.name || null,
        dept: cityObj?.dept || null,
        lat: cityObj?.lat || null,
        lon: cityObj?.lon || null,
        accepted_charter: isPro ? true : false,
      });
      toast.success(`Bienvenue ${user.name} !`);
      track(isPro ? "pro_signup_completed" : "particulier_signup_completed", {
        trade: isPro ? form.trade : undefined,
        city: cityObj?.name,
      });
      if (isPro) nav("/pro/choose-plan", { replace: true });
      else nav("/p/home", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const selectedCity = null;

  return (
    <div className="app-shell grain-bg tools-bg flex flex-col">
      <ToolsBackdrop />
      <header className="px-5 py-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="tap-scale text-plate-tight" data-testid="register-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div className="text-plate-tight">
          <div className="text-[10px] uppercase tracking-widest text-slate2">Inscription</div>
          <h2 className="font-heading font-bold text-lg">{isPro ? "Compte Professionnel" : "Compte Particulier"}</h2>
        </div>
      </header>

      <form onSubmit={submit} className="px-5 py-4 pb-10">
        <div className="auth-panel space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Nom complet</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="reg-name-input" className="pl-10 rounded-xl h-12" placeholder="Marc Dupont" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="reg-email-input" type="email" className="pl-10 rounded-xl h-12" placeholder="vous@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Mot de passe</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="reg-password-input" type="password" className="pl-10 rounded-xl h-12" placeholder="••••••••" value={form.password} onChange={(e) => set("password", e.target.value)} />
          </div>
        </div>

        {isPro && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate2">Entreprise (optionnel)</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="reg-company-input" className="pl-10 rounded-xl h-12" placeholder="Plomberie Dupont" value={form.company} onChange={(e) => set("company", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate2">Téléphone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="reg-phone-input" className="pl-10 rounded-xl h-12" placeholder="0612345678" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate2">Type</Label>
                <RadioGroup value={form.identifier_type} onValueChange={(v) => set("identifier_type", v)} className="flex gap-3 pt-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <RadioGroupItem value="siret" id="rg-siret" data-testid="reg-idtype-siret" />
                    <span className="text-sm text-ink">SIRET</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <RadioGroupItem value="siren" id="rg-siren" data-testid="reg-idtype-siren" />
                    <span className="text-sm text-ink">SIREN</span>
                  </label>
                </RadioGroup>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate2">
                Numéro {form.identifier_type.toUpperCase()} ({form.identifier_type === "siren" ? "9" : "14"} chiffres) *
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid="reg-identifier-input"
                  inputMode="numeric"
                  maxLength={form.identifier_type === "siren" ? 11 : 17}
                  className="rounded-xl h-12 font-mono flex-1"
                  placeholder={form.identifier_type === "siren" ? "123 456 789" : "123 456 789 00012"}
                  value={form.identifier}
                  onChange={(e) => { set("identifier", e.target.value); setSiretCheck(null); }}
                />
                <Button type="button" onClick={verifyIdentifier} variant="outline" className="rounded-xl h-12 shrink-0" data-testid="reg-verify-siret-btn">Vérifier</Button>
              </div>
              {siretCheck?.found && (
                <div className="bg-forest/10 border border-forest/30 rounded-xl p-3 text-xs" data-testid="reg-siret-verified">
                  <div className="font-semibold text-forest flex items-center gap-1.5">✓ Entreprise vérifiée</div>
                  <div className="text-ink font-medium">{siretCheck.denomination}</div>
                  {siretCheck.commune && <div className="text-slate2">{siretCheck.commune} · {siretCheck.code_postal}</div>}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate2">Logo entreprise (optionnel)</Label>
              <label className="block border-2 border-dashed border-border rounded-2xl p-4 text-center tap-scale cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="reg-logo-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 2 * 1024 * 1024) { toast.error("Logo trop lourd (2 Mo max)"); return; }
                    const r = new FileReader();
                    r.onload = () => set("logo", r.result);
                    r.readAsDataURL(f);
                  }}
                />
                {form.logo ? (
                  <div className="flex items-center justify-center gap-3">
                    <img src={form.logo} alt="logo" className="w-14 h-14 rounded-xl object-cover border border-border" />
                    <div className="text-sm text-ink">Logo prêt</div>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); set("logo", null); }} className="ml-2 text-slate2 tap-scale" data-testid="reg-remove-logo">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm text-slate2">
                    <Upload className="w-4 h-4" /> Ajouter mon logo
                  </div>
                )}
              </label>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate2">Métier</Label>
              <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
                <SelectTrigger className="rounded-xl h-12" data-testid="reg-trade-select"><SelectValue placeholder="Choisir un métier" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {trades.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-slate2">Ville *</Label>
          <CityCombobox value={cityObj} onChange={setCityObj} testid="reg-city" />
        </div>

        {isPro && (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate2">Rayon d'intervention</Label>
            <Select value={String(form.radius_km)} onValueChange={(v) => set("radius_km", parseInt(v))}>
              <SelectTrigger className="rounded-xl h-12" data-testid="reg-radius-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 km</SelectItem>
                <SelectItem value="15">15 km</SelectItem>
                <SelectItem value="20">20 km</SelectItem>
                <SelectItem value="30">30 km</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Button type="submit" disabled={busy} data-testid="register-submit-btn"
          className={`w-full rounded-full py-6 text-base font-semibold mt-4 ${isPro ? "bg-pro hover:bg-pro-hover text-white" : "bg-brand hover:bg-brand-hover text-white"}`}>
          {busy ? "Création…" : `Créer mon compte ${isPro ? "Pro" : "Particulier"}`}
        </Button>

        <div className="text-center text-xs text-slate2 pt-2">
          Déjà un compte ?{" "}
          <button type="button" onClick={() => nav("/login")} className="text-brand font-semibold" data-testid="goto-login-from-reg">Se connecter</button>
        </div>
        </div>
      </form>
    </div>
  );
}
